import angular from 'angular';
import olFormatGeoJSON from 'ol/format/GeoJSON'
import olSourceVector from 'ol/source/Vector'
import olLayerVector from 'ol/layer/Vector'
import olStyleStyle from 'ol/style/Style'
import olStyleFill from 'ol/style/Fill'
import olStyleStroke from 'ol/style/Stroke'

import ngeoDownloadCsv from 'ngeo/download/Csv';

import { HistogrammeD3 } from './d3Chart'

/**
 * @typedef {Object} RechargeElement
 * @property {number} annee
 * @property {number} valeur
 */

/**
 * @type {angular.IModule}
 * @hidden
 */
const myModule = angular.module(
  'apronaRechargeFooterPanelComponent',
  []
);

myModule.run(
  /**
   * @ngInject
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    // @ts-ignore: webpack
    $templateCache.put('aprona/recharge/footer/panel', require('./footerPanelComponent.html'));
  }
);

myModule.value(
  'apronaRechargeFooterPanelTemplateUrl',
  /**
   * @param {angular.IAttributes} $attrs Attributes.
   * @returns {string} The template url.
   */
  ($attrs) => {
    const templateUrl = $attrs.apronaRechargeFooterPanelTemplateUrl;
    return templateUrl !== undefined ? templateUrl : 'aprona/recharge/footer/panel';
  },
);

/**
 * @param {angular.IAttributes} $attrs Attributes.
 * @param {function(angular.IAttributes): string} apronaRechargeFooterPanelTemplateUrl Template function.
 * @returns {string} Template URL.
 * @private
 * @hidden
 */
apronaRechargeFooterPanelTemplateUrl.$inject = ['$attrs', 'apronaRechargeFooterPanelTemplateUrl'];
function apronaRechargeFooterPanelTemplateUrl($attrs, apronaRechargeFooterPanelTemplateUrl) {
  return apronaRechargeFooterPanelTemplateUrl($attrs);
}

/**
 * @hidden
 */
export class Controller {
  /**
   * @param {JQuery} $element Element.
   * @param {angular.IScope} $scope Angular scope.
   * @param {angular.IHttpService} $http Angular http service.
   * @param {String} rechargeNappeUrl
   * @ngdoc controller
   * @ngname ApronaRechargefooterPanelController
   */
  constructor($element, $scope, $http, rechargeNappeUrl) {

    // Binding properties

    /**
     * @type {?import('ol/Map').default}
     */
    this.map = null;

    /**
     * @type {?import('ol/geometry/Point').default}
     */
    this.rechargePoint = null;

    // Injected properties

    /**
     * @type {JQuery}
     * @private
     */
    this.element_ = $element;

    /**
     * @type {angular.IScope}
     * @private
     */
    this.$scope_ = $scope;

    /**
     * @type {angular.IHttpService}
     */
    this.$http_ = $http;

    /**
     * @type {String}
     * @private
     */
    this.rechargeNappeUrl_ = rechargeNappeUrl

    // Inner properties

    /**
     * @type {RechargeElement[]}
     */
    this.values = [];

    /**
     * @type {number | null}
     */
    this.normale = null;

    /**
     * @type {boolean}
     */
    this.isLoading = false;

    /**
     * @type {?import('ol/source/Vector').default}
     */
    this.vectorSource = new olSourceVector();

    /**
     * @type {?import('ol/layer/Vector').default}
     */
    this.vectorLayer = new olLayerVector({
      source: this.vectorSource,
      style: new olStyleStyle({
        fill: new olStyleFill({
          color: 'rgba(255, 0, 0, 0.3)'
        }),
        stroke: new olStyleStroke({
          color: 'red',
          width: 3
        })
      }),
      zIndex: 999
    });
  }

  /**
   * Called on initialization of the component.
   */
  $onInit() {
    this.map.getLayers().push(this.vectorLayer);

    // Watch the rechargePoint to update the rechargeData (data for the chart).
    this.$scope_.$watch(
      () => this.rechargePoint,
      (newPoint, oldPoint) => {
        if (oldPoint !== newPoint) {
          this.update_();
        }
      }
    );
  }

  update_() {
    this.clear_();

    if (this.rechargePoint) {
      this.getData_();
    }

    this.active = !!this.rechargePoint;
  }

  clear_() {
    this.isErrored = false;
    this.vectorSource.clear();
    this.values = [];
    this.normale = null;
  }

  getData_() {
    if (!this.rechargePoint) {
      throw new Error('Missing rechargePoint');
    }

    this.isLoading = true;

    const params = {
      x: this.rechargePoint.getCoordinates()[0],
      y: this.rechargePoint.getCoordinates()[1],
    }

    this.$http_({
      url: this.rechargeNappeUrl_,
      method: 'GET',
      params: params,
      paramSerializer: '$httpParamSerializerJQLike',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }).then(this.getDataSuccess_.bind(this), this.getDataError_.bind(this));
  }

  /**
   * @param {angular.IHttpResponse<ProfileServiceResult>} resp Response.
   */
  getDataSuccess_(resp) {
    const data = resp.data;
    if (data.geometry != null) {
      const polygon = new olFormatGeoJSON().readFeature(data.geometry);
      this.vectorSource.addFeature(polygon);
    }
    this.values = data.valeurs;
    this.normale = data.normale;
    this.drawChart_();
  }

  /**
   * @param {angular.IHttpResponse<never>} resp Response.
   */
  getDataError_(resp) {
    this.isLoading = false;
    this.isErrored = true;
    console.error('Can not get recharge data.');
  }

  drawChart_() {
    const histo = new HistogrammeD3({
      container: "#aprona-recharge-chart",
      data: this.values,
      normale: this.normale,
    });
  }

  downloadCsv() {
    if (this.values.length === 0) {
      return;
    }

    /** @type {import('ngeo/download/Csv').GridColumnDef[]} */
    const headers = [
      {name: 'Année'},
      {name: 'Valeur'},
    ];

    const rows = this.values.map(e => ({
      "Année": e.annee,
      "Valeur": e.valeur.toFixed(2),
    }));

    ngeoDownloadCsv.startDownload(rows, headers, 'recharge.csv');
  };
}

Controller.$inject = [
  '$element',
  '$scope',
  '$http',
  'rechargeNappeUrl'
];

myModule.component('apronaRechargeFooterPanel', {
  bindings: {
    'active': '=',
    'map': '<',
    'rechargePoint': '<',
  },
  controller: Controller,
  templateUrl: apronaRechargeFooterPanelTemplateUrl,
});

export default myModule;
